import { useMemo, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import {
  useGetUsersQuery,
  useRemoveUserFromTenantMutation,
  useUpdateTenantUserAdminMutation,
} from '@features/users';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useOutletContext } from 'react-router';

import { ConfirmDialog, InlineLoader, PageLoader } from '@/components/common';
import { logger } from '@/utils/logger';

import { InviteUsersModal } from './InviteUsersModal';

interface TenantContextType {
  tenantId: string;
  tenant: {
    tenantId: string;
    name: string;
  };
}

/**
 * Organization Users Tab
 * Displays all users in the current tenant organization
 * Admin can invite, remove, and manage user roles
 */
export const OrganizationUsers = () => {
  const { tenantId } = useOutletContext<TenantContextType>();
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
  const [removingUserId, setRemovingUserId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ id: number; username: string } | null>(null);

  // Get logged-in user's email from Auth0
  const { user: auth0User } = useAuth0();

  // Fetch all users - refetch when component mounts or args change
  const {
    data: allUsers = [],
    isLoading,
    error,
  } = useGetUsersQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  // Mutations
  const [updateUserAdmin] = useUpdateTenantUserAdminMutation();
  const [removeUser] = useRemoveUserFromTenantMutation();

  // Filter users by current tenant (handle both string and number comparison)
  const tenantUsers = allUsers.filter((u) => String(u.tenantid) === String(tenantId));

  // Sort users alphabetically
  // Note: Users without valid Identity are already filtered out by the mapper (users.mappers.ts)
  const sortedUsers = useMemo(() => {
    return [...tenantUsers].sort((a, b) => {
      const usernameA = a.username.toLowerCase();
      const usernameB = b.username.toLowerCase();
      return usernameA.localeCompare(usernameB);
    });
  }, [tenantUsers]);

  // Find current user by matching Auth0 email against username
  const currentUserEmail = auth0User?.email;
  const currentUserInTenant = tenantUsers.find((u) => u.username === currentUserEmail);
  const currentUserId = currentUserInTenant?.id;
  const isAdmin = currentUserInTenant?.admin ?? false;

  const handleToggleAdmin = async (userId: number, currentAdminState: boolean) => {
    setUpdatingUserId(userId);
    try {
      await updateUserAdmin({
        tenantId,
        userId,
        isAdmin: !currentAdminState,
      }).unwrap();
    } catch (err) {
      logger.error('Failed to update user admin status:', err);
      alert('Failed to update user role');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleRemoveUser = async (userId: number) => {
    setRemovingUserId(userId);
    try {
      await removeUser({ tenantId, userId }).unwrap();
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (err) {
      logger.error('Failed to remove user:', err);
      alert('Failed to remove user');
    } finally {
      setRemovingUserId(null);
    }
  };

  const openDeleteDialog = (user: { id: number; username: string }) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  if (isLoading) {
    return <PageLoader variant="section" />;
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">Failed to load users</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Invite Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Manage Team</Typography>
        {isAdmin && (
          <Button variant="contained" size="small" onClick={() => setInviteModalOpen(true)}>
            Invite Members
          </Button>
        )}
      </Box>

      {/* Users Table */}
      {sortedUsers.length === 0 ? (
        <Alert severity="info">
          No users found for this organization. Click "Invite Members" to add users.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Members</TableCell>
                <TableCell>Role</TableCell>
                {isAdmin && <TableCell align="center">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 50 }}>
                        {user.admin ? 'Admin' : 'User'}
                      </Typography>
                      <Tooltip
                        title={(() => {
                          if (!isAdmin) return "You don't have admin access to update role!";
                          if (user.id === currentUserId) return 'Cannot change your own role';
                          return 'Toggle admin role';
                        })()}
                      >
                        <span>
                          <Switch
                            checked={user.admin}
                            onChange={() => handleToggleAdmin(user.id, user.admin)}
                            disabled={
                              user.id === currentUserId || !isAdmin || updatingUserId === user.id
                            }
                            size="small"
                          />
                        </span>
                      </Tooltip>
                      {updatingUserId === user.id && <InlineLoader size={16} />}
                    </Box>
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                        }}
                      >
                        <Tooltip
                          title={
                            user.id === currentUserId ? 'Cannot remove yourself' : 'Remove user'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                openDeleteDialog({ id: user.id, username: user.username })
                              }
                              disabled={user.id === currentUserId || removingUserId === user.id}
                            >
                              <DeleteIcon
                                fontSize="small"
                                color={
                                  user.id === currentUserId || removingUserId === user.id
                                    ? 'disabled'
                                    : 'error'
                                }
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {removingUserId === user.id && <InlineLoader size={16} />}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Invite Users Modal */}
      <InviteUsersModal
        tenantId={tenantId}
        isOpen={inviteModalOpen}
        setIsOpen={setInviteModalOpen}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={() => userToDelete && handleRemoveUser(userToDelete.id)}
        title="Remove User"
        message={
          <>
            Are you sure you want to remove <strong>{userToDelete?.username}</strong>? This action
            cannot be undone.
          </>
        }
        confirmText="Remove User"
        confirmColor="error"
        isLoading={removingUserId !== null}
        loadingText="Removing..."
      />
    </Box>
  );
};
